// Plain-text ↔ rich-HTML helpers for the BOPPPS planner.
//
// The planner has always stored one plain string per field ("bridgeIn",
// "objective", each activity's "facilitatorActivity", …) and every downstream
// consumer reads those strings: the AI Prompt page's `loadFromPlanner`, the
// companion content pack, the print sheet, saved JSON people mail each other.
//
// Adding a formatting editor must not break any of that, so rich text lives in
// a parallel, optional `richText` map rather than replacing the plain field:
//
//   {
//     bridgeIn: "Play 'Back to Neverland'\nHand out name cards.",
//     richText: { bridgeIn: "<p>Play <strong>'Back to Neverland'</strong></p>…" }
//   }
//
// The plain string stays the canonical value and is regenerated from the HTML
// every time the modal editor saves, so a file written by the new planner still
// opens correctly in anything that only knows the old shape — and a file
// written by the old planner has no `richText` key at all, which reads here as
// "this field is plain text".

/** Fields on the lesson object that can carry rich text. */
export const RICH_TEXT_FIELDS = [
  'bridgeIn',
  'objective',
  'preAssessment',
  'postAssessment',
  'summary',
  'reflections'
];

/**
 * Fields on an activity row that can carry rich text.
 *
 * `facilitatorActivity` holds the instructor's notes and `learnerActivity`
 * what the students do — the names match the AI Prompt page's, which has had
 * both since long before the planner did. `materialsRequired` and
 * `learnerMaterials` split the same way: what you set up versus what the
 * students need in front of them.
 *
 * An activity's `title` is deliberately absent: it is a one-line name, and a
 * heading with its own font and colour is not what that field is for.
 */
export const RICH_TEXT_ACTIVITY_FIELDS = [
  'facilitatorActivity',
  'learnerActivity',
  'materialsRequired',
  'learnerMaterials'
];

const HTML_ENTITIES = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;'
};

/** Escape a plain string for interpolation into an HTML template. */
export const escapeHtml = (value) => {
  if (value === undefined || value === null) return '';
  return String(value).replace(/[&<>"']/g, (char) => HTML_ENTITIES[char]);
};

/** True when the string carries markup rather than being plain prose. */
export const isHtmlContent = (value) => typeof value === 'string' && /<[a-z][\s\S]*>/i.test(value);

/**
 * True when an HTML fragment has no visible content. TipTap hands back
 * `<p></p>` for an empty document, which must not count as "this field has
 * formatting" or every untouched field would render as a blank preview.
 */
export const isBlankHtml = (value) => {
  if (typeof value !== 'string' || !value.trim()) return true;
  const withoutTags = value
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, '');
  return withoutTags.length === 0;
};

/**
 * True when an HTML fragment carries formatting — anything beyond the
 * paragraphs and line breaks `plainTextToHtml` produces from typed text.
 *
 * This is what lets a field whose only storage is one HTML string still behave
 * like the planner's: prose the user typed stays an editable textarea, and a
 * field that has been through the formatting editor (or arrived formatted from
 * the planner) renders what it means instead of showing its tags.
 */
export const isFormattedHtml = (value) => {
  if (typeof value !== 'string' || isBlankHtml(value)) return false;
  // Bare <p>/<br> only — attributes on them (alignment, colour) are
  // formatting, so the match is deliberately for the plain forms.
  const withoutPlainWrappers = value.replace(/<\/?(?:p|br)\s*\/?>/gi, '');
  return /<[a-z][^>]*>/i.test(withoutPlainWrappers);
};

const decodeEntities = (value) => value
  .replace(/&nbsp;/gi, ' ')
  .replace(/&lt;/gi, '<')
  .replace(/&gt;/gi, '>')
  .replace(/&quot;/gi, '"')
  .replace(/&#0*39;/gi, "'")
  .replace(/&apos;/gi, "'")
  // Ampersand last, so "&amp;lt;" does not become "<".
  .replace(/&amp;/gi, '&');

const tidyPlainText = (value) => value
  .replace(/\r\n/g, '\n')
  .replace(/[ \t]+\n/g, '\n')
  .replace(/\n{3,}/g, '\n\n')
  .replace(/[ \t]{2,}/g, ' ')
  .trim();

/**
 * Flatten an HTML fragment to the plain string the rest of the app reads.
 *
 * List items keep a marker so a bulleted objective list still looks like a
 * list once it reaches the AI prompt or the saved JSON — dropping the bullets
 * would turn four objectives into one run-on paragraph.
 */
export const htmlToPlainText = (value) => {
  if (typeof value !== 'string' || !value) return '';
  if (!isHtmlContent(value)) return tidyPlainText(value);

  let working = value.replace(/<(script|style)[\s\S]*?<\/\1>/gi, '');

  // Numbering first, while the <ol> boundaries are still there to scope it:
  // each list restarts at 1 rather than counting through the whole field.
  working = working.replace(/<ol[^>]*>([\s\S]*?)<\/ol>/gi, (match, inner) => {
    let index = 0;
    const numbered = inner.replace(/<li[^>]*>/gi, () => {
      index += 1;
      return `${index}. `;
    });
    return `<ol>${numbered}</ol>`;
  });
  working = working.replace(/<li[^>]*>/gi, '• ');

  working = working
    .replace(/<br\s*\/?>/gi, '\n')
    // Block-level content is separated by a blank line, list items by one
    // newline. Without that distinction a two-paragraph field flattens into
    // one run-on block the moment it goes through the editor.
    .replace(/<\/(p|div|h[1-6]|blockquote|pre|ul|ol)>/gi, '\n\n')
    .replace(/<\/(li|tr)>/gi, '\n');

  const text = working.replace(/<[^>]+>/g, '');
  return tidyPlainText(decodeEntities(text))
    // A list sits directly under the sentence that introduces it, rather than
    // floating a blank line away from it.
    .replace(/\n\n(?=(?:• |\d+\. ))/g, '\n');
};

/**
 * Turn a plain string into the HTML the modal editor opens with. Blank lines
 * separate paragraphs; single newlines become <br>, which is how the textarea
 * looked on screen.
 */
export const plainTextToHtml = (value) => {
  const text = typeof value === 'string' ? value.replace(/\r\n/g, '\n') : '';
  if (!text.trim()) return '';
  return text
    .split(/\n{2,}/)
    .map((paragraph) => `<p>${escapeHtml(paragraph).replace(/\n/g, '<br>')}</p>`)
    .join('');
};

/**
 * The HTML for one field in a print sheet or an HTML export.
 *
 * Rich HTML is passed through untouched (callers sanitize); plain text is
 * escaped and its newlines turned into <br>, which is exactly what the old
 * `formatText` did minus the escaping gap.
 */
export const fieldToHtml = (plainValue, richHtml) => {
  if (typeof richHtml === 'string' && !isBlankHtml(richHtml)) return richHtml;
  if (Array.isArray(plainValue)) {
    return plainValue.map((item) => escapeHtml(item)).join('<br>');
  }
  if (plainValue === undefined || plainValue === null || plainValue === '') return '';
  return escapeHtml(plainValue).replace(/\n/g, '<br>');
};

/** Read the rich HTML stored for a field, or '' when the field is plain text. */
export const getRichText = (holder, field) => {
  const map = holder && holder.richText;
  if (!map || typeof map !== 'object') return '';
  const html = map[field];
  return typeof html === 'string' && !isBlankHtml(html) ? html : '';
};

/**
 * Write (or clear) the rich HTML for a field on a lesson or activity object.
 *
 * Clearing removes the key, and removes the whole `richText` map once it is
 * empty, so a plan with no formatting saves as exactly the JSON the old
 * planner wrote.
 */
export const setRichText = (holder, field, html) => {
  const existing = holder && typeof holder.richText === 'object' && holder.richText ? holder.richText : {};
  const next = { ...existing };
  if (typeof html === 'string' && !isBlankHtml(html)) {
    next[field] = html;
  } else {
    delete next[field];
  }
  const result = { ...holder };
  if (Object.keys(next).length) {
    result.richText = next;
  } else {
    delete result.richText;
  }
  return result;
};

/**
 * Keep only known rich-text keys with string values. Loaded files are
 * untrusted, and an unexpected key here would be carried straight back out
 * into the next export.
 */
export const normalizeRichTextMap = (map, allowedFields) => {
  if (!map || typeof map !== 'object' || Array.isArray(map)) return null;
  const result = {};
  allowedFields.forEach((field) => {
    const html = map[field];
    if (typeof html === 'string' && !isBlankHtml(html)) result[field] = html;
  });
  return Object.keys(result).length ? result : null;
};
